<!-- src/components/forms/completed/SingleItemInspectionCompleted.vue -->
<template>
  <div class="space-y-12 text-left animate-fadeIn">
    
    <!-- 1. ITEM DETAILS SUMMARY -->
    <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-200 shadow-inner">
      <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
        <ShieldCheckIcon class="h-4 w-4 text-[#20FCF1]" /> 1. Item Details & Operational Context
      </h3>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Asset ID</p>
          <p class="text-xs font-black text-[#0A1630] uppercase mt-1">{{ submissionData.itemDetails?.assetId || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Week Commencing</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.weekCommencing || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Description</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.description || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Serial / CAT No.</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.serialNo || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Site / Location</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.siteLocation || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Risk Rating</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.riskRating || 'Low' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Primary Inspector</p>
          <p class="text-xs font-bold text-slate-700 uppercase mt-1">{{ submissionData.itemDetails?.primaryInspector || 'N/A' }}</p>
        </div>
        <div>
          <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest">Competence Confirmed</p>
          <p class="text-xs font-bold uppercase mt-1" :class="submissionData.itemDetails?.competenceConfirmed ? 'text-emerald-700' : 'text-rose-600'">
            {{ submissionData.itemDetails?.competenceConfirmed ? 'Yes' : 'No' }}
          </p>
        </div>
      </div>
    </div>

    <!-- 2. DAILY CHECKLIST MATRIX -->
    <div class="space-y-4">
      <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em] ml-1">2. Daily Inspection Checklist (Monday – Sunday)</h3>
      <div class="overflow-x-auto rounded-[24px] border border-slate-200 bg-white shadow-sm">
        <table class="min-w-full divide-y divide-slate-200 text-left">
          <thead class="bg-[#0A1630] text-white">
            <tr>
              <th class="px-5 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Check Item</th>
              <th v-for="d in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']" :key="d" class="px-2 py-3 text-[9px] font-black text-center uppercase tracking-widest">{{ d }}</th>
              <th class="px-4 py-3 text-[9px] font-black text-[#20FCF1] uppercase tracking-widest">Comments</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr v-for="(item, idx) in (submissionData.checklist || [])" :key="idx" class="hover:bg-slate-50">
              <td class="px-5 py-3 text-[11px] font-bold text-slate-700 uppercase">{{ idx + 1 }}. Check Item #{{ idx + 1 }}</td>
              <td v-for="d in ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']" :key="d" class="px-2 py-3 text-center">
                <span class="inline-block w-4 h-4 rounded text-[10px] font-black" :class="item[d] ? 'bg-emerald-100 text-emerald-800' : 'text-slate-300'">
                  {{ item[d] ? '✓' : '–' }}
                </span>
              </td>
              <td class="px-4 py-3 text-xs font-medium text-slate-600 italic">{{ item.comments || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 3. DAILY RESULTS -->
    <div class="space-y-4">
      <h3 class="text-xs font-black text-[#0A1630] uppercase tracking-[0.2em] ml-1">3. Daily Overall Result</h3>
      <div class="grid grid-cols-7 gap-2">
        <div v-for="(res, day) in (submissionData.dailyResults || {})" :key="day" class="p-4 rounded-2xl border text-center shadow-sm" :class="res === 'PASS' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : (res === 'FAIL' ? 'bg-rose-50 border-rose-200 text-rose-800' : 'bg-amber-50 border-amber-200 text-amber-800')">
          <p class="text-[9px] font-black uppercase tracking-widest">{{ day }}</p>
          <p class="text-xs font-black uppercase mt-1">{{ res }}</p>
        </div>
      </div>
    </div>

    <!-- 4. DEFECTS & EVIDENCE -->
    <div v-if="submissionData.defectsDetails || submissionData.defectEvidenceUrls?.length" class="bg-rose-50/50 p-6 rounded-[28px] border border-rose-100 space-y-4">
      <h3 class="text-xs font-black text-rose-800 uppercase tracking-[0.2em]">4. Defects Found / Actions Taken</h3>
      <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.defectsDetails || 'No defects recorded.' }}"</p>
      
      <div v-if="submissionData.defectEvidenceUrls?.length" class="flex flex-wrap gap-4 pt-2">
        <a v-for="(url, idx) in submissionData.defectEvidenceUrls" :key="idx" :href="url" target="_blank" class="block h-20 w-20 rounded-xl overflow-hidden border shadow-sm hover:scale-105 transition-all">
          <img :src="url" class="w-full h-full object-cover" alt="Defect Evidence" />
        </a>
      </div>
    </div>

    <!-- 5. SIGN-OFF -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-slate-100">
      <div class="bg-slate-50 p-6 rounded-3xl border border-slate-200">
        <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Inspector Sign-Off</p>
        <p class="text-sm font-black text-[#0A1630] uppercase">{{ submissionData.signOff?.inspectorName || 'Inspector' }}</p>
        <div v-if="submissionData.signOff?.inspectorSig" class="mt-4 bg-white p-3 rounded-2xl border inline-block">
          <img :src="submissionData.signOff.inspectorSig" class="h-12 object-contain grayscale" alt="Inspector Signature" />
        </div>
      </div>
      <div v-if="submissionData.signOff?.supervisorName" class="bg-slate-50 p-6 rounded-3xl border border-slate-200">
        <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Supervisor Sign-Off</p>
        <p class="text-sm font-black text-[#0A1630] uppercase">{{ submissionData.signOff.supervisorName }}</p>
        <div v-if="submissionData.signOff?.supervisorSig" class="mt-4 bg-white p-3 rounded-2xl border inline-block">
          <img :src="submissionData.signOff.supervisorSig" class="h-12 object-contain grayscale" alt="Supervisor Signature" />
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ShieldCheckIcon } from '@heroicons/vue/24/outline';
defineProps({
  submissionData: { type: Object, required: true }
});
</script>